// client/src/components/Insights/tabs/ChatTab.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, TextField, InputAdornment, IconButton, Chip, Avatar, Fade, useTheme } from '@mui/material';
import { Brain, Send } from 'lucide-react';
import { useCurrency } from '../../../contexts/CurrencyContext';
import { GlassCard, TypingDots } from '../shared/SharedComponents';
import { GRADIENT, alpha, gradientTextSx, SUGGESTED_QUESTIONS } from '../shared/constants';
import api from '../../../services/api';

const ChatTab = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { formatAmount } = useCurrency();
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isThinking, setIsThinking] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  const addWelcomeMessage = () => {
    setMessages([{
      role: 'ai',
      content: t('aiInsights.chat.description', 'Ask me anything about your finances. I analyze your real transaction data to give you personalized, actionable intelligence.'),
      timestamp: new Date(),
      followUps: [],
    }]);
  };

  useEffect(() => {
    if (historyLoaded) return;
    const token = localStorage.getItem('token');
    if (!token) { addWelcomeMessage(); setHistoryLoaded(true); return; }
    (async () => {
      try {
        const { data } = await api.get('/user/cortex/history');
        if (data.success && data.conversations && data.conversations.length > 0) {
          setMessages(data.conversations.map(c => ({ role: c.role, content: c.content, timestamp: new Date() })));
        } else {
          addWelcomeMessage();
        }
      } catch {
        addWelcomeMessage();
      } finally {
        setHistoryLoaded(true);
      }
    })();
  }, [historyLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isThinking]);

  const sendMessage = useCallback(async (text) => {
    const trimmed = (text || inputValue).trim();
    if (!trimmed || isThinking) return;
    setInputValue('');
    setMessages(prev => [...prev, { role: 'user', content: trimmed, timestamp: new Date() }]);
    setIsThinking(true);
    try {
      const { data } = await api.post('/user/cortex/chat', { message: trimmed });
      const answer = data.answer || data.message || data.response || t('aiInsights.chat.emptyAnswer', 'I could not find an answer to that. Try asking it another way.');
      const followUps = data.followUpQuestions || data.suggestions || [];
      setMessages(prev => [...prev, { role: 'ai', content: answer, timestamp: new Date(), followUps }]);
    } catch {
      setMessages(prev => [...prev, {
        role: 'ai',
        content: t('aiInsights.chat.errorMessage', 'I had trouble connecting. Please try again.'),
        timestamp: new Date(),
        followUps: [],
      }]);
    } finally {
      setIsThinking(false);
      inputRef.current?.focus();
    }
  }, [inputValue, isThinking, t]);

  const isEmpty = messages.length === 0;
  const suggestions = SUGGESTED_QUESTIONS.map(({ cat, q }, i) => ({ cat, q: t(`aiInsights.chat.suggestions.q${i + 1}`, q) }));

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: { xs: '75vh', md: '70vh' } }}>
      {isEmpty && (
        <Fade in>
          <Box sx={{ textAlign: 'center', py: { xs: 4, md: 6 }, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
            <Box sx={{ width: 80, height: 80, borderRadius: '50%', background: GRADIENT, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3, boxShadow: `0 0 40px ${alpha('#8b5cf6', 0.3)}` }}>
              <Brain size={40} color="#fff" />
            </Box>
            <Typography variant="h4" fontWeight={900} sx={gradientTextSx} gutterBottom>
              {t('aiInsights.chat.title', 'Your Personal CFO')}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: '#10b981', animation: 'pulse 2s infinite', '@keyframes pulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.4 } } }} />
              <Typography variant="body2" color="text.secondary">{t('aiInsights.chat.subtitle', 'Powered by AI · Analyzing your financial data')}</Typography>
            </Box>
            <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 480, mb: 4 }}>
              {t('aiInsights.chat.description', 'Ask me anything about your finances. I analyze your real transaction data to give you personalized, actionable intelligence.')}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center', maxWidth: 600 }}>
              {suggestions.map(({ cat, q }) => (
                <Chip key={q} label={`${cat} ${q}`} onClick={() => sendMessage(q)} size="small"
                  sx={{ cursor: 'pointer', fontSize: 12, fontWeight: 500, background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(99,102,241,0.06)',
                    border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(99,102,241,0.15)'}`,
                    '&:hover': { background: alpha('#8b5cf6', 0.15), borderColor: '#8b5cf6' },
                    transition: 'all 0.2s ease' }} />
              ))}
            </Box>
          </Box>
        </Fade>
      )}

      {!isEmpty && (
        <Box sx={{ flex: 1, overflowY: 'auto', px: { xs: 1, md: 2 }, py: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {messages.map((msg, i) => (
            <Fade in key={i}>
              <Box sx={{ display: 'flex', justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start', gap: 1.5, maxWidth: '100%' }}>
                {msg.role === 'ai' && (
                  <Avatar sx={{ background: GRADIENT, width: 36, height: 36, flexShrink: 0, boxShadow: `0 0 12px ${alpha('#8b5cf6', 0.3)}` }}>
                    <Brain size={18} color="#fff" />
                  </Avatar>
                )}
                <Box sx={{ maxWidth: '78%' }}>
                  <Box sx={{
                    px: 2.5, py: 2, borderRadius: msg.role === 'user' ? '20px 20px 4px 20px' : '20px 20px 20px 4px',
                    background: msg.role === 'user' ? GRADIENT : (isDark ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.95)'),
                    border: msg.role === 'ai' ? `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)'}` : 'none',
                    boxShadow: msg.role === 'ai' ? '0 2px 12px rgba(0,0,0,0.08)' : '0 2px 8px rgba(99,102,241,0.25)',
                    backdropFilter: msg.role === 'ai' ? 'blur(12px)' : 'none',
                  }}>
                    <Typography variant="body2" color={msg.role === 'user' ? '#fff' : 'text.primary'}
                      sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.7, fontSize: 13.5 }}>{msg.content}</Typography>
                  </Box>
                  <Typography variant="caption" sx={{ opacity: 0.5, mt: 0.5, display: 'block', textAlign: msg.role === 'user' ? 'right' : 'left', px: 1 }}>
                    {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </Typography>
                  {msg.role === 'ai' && msg.followUps?.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1, px: 0.5 }}>
                      {msg.followUps.slice(0, 3).map((fu, j) => (
                        <Chip key={j} label={fu} size="small" onClick={() => sendMessage(fu)}
                          sx={{ cursor: 'pointer', fontSize: 11, background: alpha('#8b5cf6', 0.08), border: `1px solid ${alpha('#8b5cf6', 0.2)}`,
                            '&:hover': { background: alpha('#8b5cf6', 0.18) } }} />
                      ))}
                    </Box>
                  )}
                </Box>
              </Box>
            </Fade>
          ))}
          {isThinking && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Avatar sx={{ background: GRADIENT, width: 36, height: 36 }}><Brain size={18} color="#fff" /></Avatar>
              <GlassCard sx={{ px: 2, py: 1 }}>
                <TypingDots />
              </GlassCard>
            </Box>
          )}
          <div ref={bottomRef} />
        </Box>
      )}

      <Box sx={{ p: 2, borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}` }}>
        {!isEmpty && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
            {suggestions.slice(0, 4).map(({ cat, q }) => (
              <Chip key={q} label={`${cat} ${q}`} size="small" onClick={() => sendMessage(q)}
                sx={{ cursor: 'pointer', fontSize: 10, background: alpha('#8b5cf6', 0.06),
                  '&:hover': { background: alpha('#8b5cf6', 0.15) } }} />
            ))}
          </Box>
        )}
        <TextField fullWidth size="small" placeholder={t('aiInsights.chat.placeholder', 'Ask your personal CFO anything...')}
          value={inputValue} onChange={e => setInputValue(e.target.value)} inputRef={inputRef}
          onKeyDown={e => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), sendMessage())}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => sendMessage()} disabled={isThinking || !inputValue.trim()}
                  sx={{ background: inputValue.trim() ? GRADIENT : alpha('#8b5cf6', 0.1), color: inputValue.trim() ? '#fff' : '#8b5cf6',
                    '&:hover': { opacity: 0.85 }, borderRadius: 2, transition: 'all 0.3s ease' }}>
                  <Send size={16} />
                </IconButton>
              </InputAdornment>
            ),
            sx: { borderRadius: 3, '&.Mui-focused fieldset': { borderColor: '#8b5cf6 !important', boxShadow: `0 0 0 2px ${alpha('#8b5cf6', 0.15)}` } }
          }}
        />
      </Box>
    </Box>
  );
};

export default ChatTab;
